import {HttpErrorResponse} from '@angular/common/http';
import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {ApiResponse, CompoApiService, Environment, HTTP_STATUS, InitData, LOCAL_STORAGE, SESSION_STORAGE, StoreService} from 'compob2b-libs';
import {BehaviorSubject, Observable, throwError,} from 'rxjs';
import {catchError, switchMap, take, tap} from 'rxjs/operators';
import {CompoToken} from '../interfaces/CompoToken';
import {ImpersonateData} from '../interfaces/ImpersonateData';
import {JwtToken} from '../interfaces/JwtToken';
import {SignData} from '../interfaces/SignData';
import {TokensData} from '../interfaces/TokensData';
import {AuthStateService} from './auth-state.service';

const TOKEN_NAME = 'compob2bauth';
const REFRESH_TOKEN_NAME = 'compob2refresh';
const ROLE_ANONYMOUS = 'ROLE_ANONYMOUS';
const ROLE_ACCOUNT = 'ROLE_ACCOUNT';
const ROLE_MANAGER = 'ROLE_MANAGER';
const ROLE_ADMIN = 'ROLE_ADMIN';
const ROLE_SUB_ACCOUNT = 'ROLE_SUB_ACCOUNT';
const ROLE_SUPER_ADMIN = 'ROLE_SUPER_ADMIN';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  loading = false;
  environment = inject(Environment);
  apiService = inject(CompoApiService);
  stateService = inject(AuthStateService);
  router = inject(Router);
  storeService = inject(StoreService);
  jwtHelper = inject(JwtHelperService);
  private localStorage = inject(LOCAL_STORAGE);
  // sessionStorage используется ТОЛЬКО в clearStorage() для defensive sweep на
  // logout — токены пишутся в localStorage. Не drop'аем: legacy data могла
  // оказаться там до миграции, и зачищать её при выходе правильно.
  private sessionStorage = inject(SESSION_STORAGE);
  private platformId = inject(PLATFORM_ID);

  private readonly authenticated = new BehaviorSubject<boolean>(false);
  private isRefreshing = false;

  static tokenGetter() {
    if (typeof localStorage === 'undefined' || typeof localStorage.getItem !== 'function') {
      return null;
    }
    return localStorage.getItem(TOKEN_NAME);
  }

  setRefreshing(refreshing): void {
    this.isRefreshing = refreshing;
  }

  getRefreshing(): boolean {
    return this.isRefreshing;
  }

  refreshToken(): Observable<ApiResponse<TokensData> | Error> {
    const refreshToken: string | null = this.getRefreshToken();

    /**
     * If we have expired refresh token and we want user to stay at portal
     * without logging out - we need renew anonymous tokens data.
     *
     * Все ветки return явно — иначе admin-app fall-through (logout без
     * isOpenCabinet) попадал бы в нижеследующий payload-assembly с
     * refreshToken=null. Bug словлен ревью: после logout() мы не должны
     * собирать /refresh/ payload и тем более не должны заходить в
     * getAnonymousTokens() — admin context просит явный sign-in flow.
     */
    if (!refreshToken || this.jwtHelper.isTokenExpired(refreshToken)) {
      if (this.environment.isOpenCabinet) {
        return this.getAnonymousTokens();
      }
      this.logout();
      return throwError(() => new Error('Refresh token missing or expired (admin context)'));
    }

    // refreshToken здесь гарантированно non-null и non-expired.
    const refresh: CompoToken = {
      token: refreshToken,
      info: 'refresh'
    };

    return this.apiService
      .post<CompoToken, TokensData>('/refresh/', refresh)
      .pipe(
        tap(_ => this.setRefreshing(true)),
        tap(response => this.successHandler(response, true))
      );
  }

  initCustomer(): Observable<ApiResponse<InitData>> {
    return this.storeService.initData();
  }

  /**
   * Manager can impersonate any user with
   * managers token and user's id
   *
   * @param data
   * @private
   */
  public impersonate(data: ImpersonateData) {
    return this.apiService.post<ImpersonateData, TokensData>('/impersonate', data).pipe(
      tap((response: ApiResponse<TokensData>) =>
        this.successHandler(response, false)
      ),
      switchMap(_ => this.storeService.initData()),
      catchError(error => this.errorHandler(error))
    );
  }

  /**
   * Calls from sign-in.component
   * Signing in and doing other operations
   * after successful authorization: save tokens, initialize storage data, redirect to cabinet
   * @param form sing in form controls
   */
  public signIn(form: SignData): Observable<ApiResponse<InitData> | Error> {
    this.loading = true;
    this.clearStorage();

    return this.apiService.post<SignData, TokensData>('/sign-in/', form).pipe(
      take(1),
      tap((response: ApiResponse<TokensData>) =>
        this.successHandler(response, form.remember)
      ),
      switchMap(_ => this.storeService.initData()),
      catchError(error => this.errorHandler(error))
    );
  }

  /**
   *  Get special anonymous tokens for unauthorized users
   */
  public checkAndSetAnonymousToken(): Observable<ApiResponse<InitData> | Error> {

    const refreshToken = this.getRefreshToken();

    // `isTokenExpired(null)` в @auth0/angular-jwt возвращает false — пустой
    // refreshToken без явного null-check проскакивал бы валидацию и юзер с
    // живым access-токеном но БЕЗ refresh-токена не получал бы anon-инициализацию.
    if (this.getToken() === null || !refreshToken || this.jwtHelper.isTokenExpired(refreshToken)) {
      return this.authorizeAsAnonymousAndInit();
    }

    return null;
  }

  private authorizeAsAnonymousAndInit(): Observable<ApiResponse<InitData> | Error> {
    return this.getAnonymousTokens().pipe(
      switchMap(_ => this.storeService.initData()),
      catchError(error => this.errorHandler(error))
    );
  }

  private getAnonymousTokens(): Observable<ApiResponse<TokensData> | Error> {
    return this.apiService.get<TokensData>('/auth-anonymous').pipe(
      tap((response: ApiResponse<TokensData>) =>
        this.successHandler(response, true)
      ),
      catchError(error => this.errorHandler(error)),
    );
  }

  private setToken(token: string, remember: boolean): void {
    this.localStorage.setItem(TOKEN_NAME, token);
    this.localStorage.setItem('remember', 'true');
  }

  /**
   * Handles success result of request
   * @param remember boolean - remember me checkbox value
   * @param response ApiResponse
   */
  public successHandler(response: ApiResponse<TokensData>, remember: boolean): void {
    this.setAuthenticated(true);
    this.setRefreshing(false);
    this.loading = false;
    this.storeTokens(
      response.data.access.token,
      response.data.refresh.token,
      remember
    );
  }

  private storeTokens(
    accessToken: string,
    refreshToken: string,
    remember: boolean
  ) {
    this.setToken(accessToken, remember);
    this.setRefreshToken(refreshToken);
  }

  getToken(): string | null {
    return this.getItemFromStorage(TOKEN_NAME);
  }

  /**
   * BL-011: истёк ли текущий access-токен. Разделитель двух 403, которые бэк не различает
   * (анонимный/протухший → Http403ForbiddenEntryPoint == «нет прав»): протух → true (нужен
   * рефреш/релогин), валидный токен без роли → false («нет прав», popup без logout — loop-fix).
   * Нет токена → false (interceptor добавляет хедер только при наличии токена).
   */
  public isAccessTokenExpired(): boolean {
    const token = this.getToken();
    return !!token && this.jwtHelper.isTokenExpired(token);
  }

  getRoles(): string[] {
    const token = this.getDecodedToken();

    return token?.roles || [];
  }

  private getDecodedToken(): JwtToken | null {
    const token = this.getToken();
    return token ? this.jwtHelper.decodeToken<JwtToken>(token) : null;
  }

  public isAnonymous(): boolean {
    return !this.getRoles()?.length || this.getRoles().includes(ROLE_ANONYMOUS);
  }

  /**
   * Sync читалка current auth state. ВСЕ role-check'и ниже используют именно
   * её, а не `isAuthenticated()` — последняя возвращает Observable<boolean>,
   * который в булевом контексте `&&` всегда truthy (это объект). Использование
   * `isAuthenticated() && roles.includes(...)` тихо превращалось в `roles.includes(...)`
   * без проверки токена — критичный пре-existing баг авторизации.
   *
   * Считаем по JWT напрямую, а не по `this.authenticated` BehaviorSubject:
   * на bootstrap (`COMPO_APP_INITIALIZER`) роли проверяются ДО того, как
   * AuthGuard когда-либо вызовет `isAuthenticated()` и переключит subject в true.
   * Returning user с валидным token+refresh в localStorage иначе считался
   * unauthenticated на init → init() exit'нул бы без `storeService.initData()`
   * и сессия осталась бы в полу-сломанном состоянии.
   */
  private isAuthenticatedSync(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    try {
      // @auth0/angular-jwt: `isTokenExpired` возвращает false для JWT без
      // `exp` claim (`_getTokenExpirationDate` отдаёт null → `_isTokenExpired`
      // на null = "never expired"). Это даёт permanent admin любому само-
      // составленному JWT без exp. Принудительно требуем exp.
      const decoded = this.jwtHelper.decodeToken<{ exp?: number }>(token);
      if (!decoded || typeof decoded.exp !== 'number') {
        return false;
      }
      return !this.jwtHelper.isTokenExpired(token);
    } catch {
      return false;
    }
  }

  public isAccount(): boolean {
    return this.isAuthenticatedSync() && this.getRoles().includes(ROLE_ACCOUNT);
  }

  public isSubAccount(): boolean {
    return this.isAuthenticatedSync() && this.getRoles().includes(ROLE_SUB_ACCOUNT);
  }

  public isCustomer(): boolean {
    return this.isAuthenticatedSync() && (this.getRoles().includes(ROLE_ACCOUNT) || this.getRoles().includes(ROLE_SUB_ACCOUNT));
  }

  public isManager(): boolean {
    return this.isAuthenticatedSync() && this.getRoles().includes(ROLE_MANAGER);
  }

  public isAdmin(): boolean {
    return this.isAuthenticatedSync() && (this.getRoles().includes(ROLE_SUPER_ADMIN) || this.getRoles().includes(ROLE_ADMIN));
  }

  public isSuperAdmin(): boolean {
    return this.isAuthenticatedSync() && this.getRoles().includes(ROLE_SUPER_ADMIN);
  }

  public isAuthenticated(): Observable<boolean> {
    const token: string | null = this.getToken();

    if (!token) {
      // SSR: localStorage = noopStorage, токена не будет НИКОГДА. Не дёргаем
      // abort/refresh, чтобы не идти в /auth-anonymous лишний раз — anonAuthInterceptor
      // обслуживает Authorization-header отдельным per-instance кэшем (anon-token.cache.ts).
      // Возвращаем true ТОЛЬКО для allowAnonymousView-окружений (customer),
      // чтобы AuthGuardService пропустил navigation; SSR рендерит публичный layout.
      // Реальные роли проверятся через getRoles() — они вернут [] и приватные
      // части UI не отрисуются. Для admin-сборки (isOpenCabinet=false) на SSR
      // тоже false-нет токена — следуем стандартному abort+redirect-flow.
      if (!isPlatformBrowser(this.platformId)) {
        if (this.environment.isOpenCabinet) {
          this.setAuthenticated(true);
          return this.getAuthenticated();
        }
        // SSR + admin: нечем сделать redirect-to-sign-in красиво, просто
        // отдаём false — AuthGuard вернёт false, Angular SSR выпишет 302 на /.
        this.setAuthenticated(false);
        return this.getAuthenticated();
      }
      // Browser: нет токена. Для openCabinet (customer) — просто метим как
      // unauthenticated и выходим, AuthGuard упадёт на anonymous-флоу через
      // checkAndSetAnonymousToken(). Для !openCabinet (admin) — abort+logout
      // (redirect to sign-in).
      // ВАЖНО: return после abort обязателен. Без него код проваливается ниже
      // в decodeToken('') → null → abort(true) → logout() → window.location.reload()
      // и при лежащем anon-endpoint получаем infinite reload loop.
      this.abort(!this.environment.isOpenCabinet);
      return this.getAuthenticated();
    }

    /**
     * If token is expired - try to refresh or logout.
     * Auth guard catches this observable and launches subscription when
     * returning result of CanActivate method.
     *
     * Принудительно требуем `exp` claim в JWT: `@auth0/angular-jwt`'s
     * `isTokenExpired` для tokens без exp возвращает false (never-expired),
     * что давало permanent admin любому самостоятельно-составленному JWT.
     * Та же защита продублирована в isAuthenticatedSync(); здесь — для
     * AuthGuardService-Observable-пути.
     */
    try {
      const decoded = this.jwtHelper.decodeToken<{ exp?: number }>(token);
      if (!decoded || typeof decoded.exp !== 'number') {
        this.abort(true);
        return this.getAuthenticated();
      }
      if (this.jwtHelper.isTokenExpired(token)) {
        return this.refreshToken().pipe(
          switchMap(_ => this.getAuthenticated())
        );
      } else {
        this.setAuthenticated(true);
      }
    } catch (e) {
      console.error(e);
      this.abort(true);
    }

    return this.getAuthenticated();
  }

  private abort(logout: boolean): void {
    this.setAuthenticated(false);
    if (logout) {
      this.logout();
    }
  }

  private getAuthenticated(): Observable<boolean> {
    return this.authenticated.asObservable();
  }

  private setAuthenticated(authenticated: boolean): void {
    this.authenticated.next(authenticated);
  }

  logout(): void {
    if (!this.environment.isOpenCabinet) {
      this.setAuthenticated(false);
      this.setRefreshing(false);
      this.clearStorage();
      this.redirectToSignIn();
    } else {
      this.clearStorage();
      // window.location.reload() — browser-only. На SSR этот же путь может
      // отработать через interceptor при 401, и без guard'а сорвёт рендер.
      if (isPlatformBrowser(this.platformId)) {
        window.location.reload();
      }
    }
  }

  clearStorage(): void {
    this.localStorage.removeItem(TOKEN_NAME);
    this.localStorage.removeItem(REFRESH_TOKEN_NAME);
    this.localStorage.removeItem('remember');
    this.localStorage.removeItem('user');
    this.localStorage.removeItem('settings');
    this.sessionStorage.clear();
  }

  redirectToSignIn(): void {
    this.router.navigate(['sign-in']);
  }

  private errorHandler(errorResponse: HttpErrorResponse): Observable<Error> {
    this.loading = false;

    const forbidden = errorResponse.status === HTTP_STATUS.FORBIDDEN
      || errorResponse.status === HTTP_STATUS.UNAUTHORIZED;

    if (!forbidden) {
      this.clearStorage();
    }

    // Одна ошибка на попытку: блокировка приоритетнее (её сообщение вытесняет остальные).
    if (errorResponse.headers.get('compo-info') === 'USER_LOCKED') {
      this.stateService.emitError('locked');
    } else if (forbidden) {
      this.stateService.emitError('forbidden');
    } else {
      this.stateService.emitError('server');
    }

    return throwError(errorResponse);
  }

  public getRefreshToken(): string | null {
    return this.getItemFromStorage(REFRESH_TOKEN_NAME);
  }

  private setRefreshToken(refreshToken: string) {
    // sessionStorage branch исторически был под `isRemember()`, но `isRemember()`
    // всегда возвращал true — sessionStorage не использовалась. Если когда-то
    // понадобится session-only persistence, восстановить через явный флаг
    // и проводить его через successHandler → storeTokens → setRefreshToken.
    this.localStorage.setItem(REFRESH_TOKEN_NAME, refreshToken);
  }

  private getItemFromStorage(name: string): string | null {
    return this.localStorage.getItem(name);
  }
}
